/**
 * You > Usage > Providers (G4 / V24): what each provider, model and account really costs per
 * verified outcome. Subscription price per account is optional (blank = unknown); reported,
 * estimated and missing costs are always labelled; every row drills to its runs.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { useEffect, useMemo, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { type ExecutionRun, executionEfficiency } from '../lib/execution-efficiency.js'
import {
  loadBilling,
  loadSyncedBilling,
  saveBilling,
  saveSyncedBilling,
} from '../lib/provider-billing.js'
import {
  analyzeProviderEfficiency,
  type Billing,
  type EfficiencyGroup,
  type EfficiencyRun,
  type EfficiencyTask,
  exportRoutingRecommendations,
} from '../lib/provider-efficiency.js'
import { useMachines, useMetrics, useProjects, useRuns } from '../lib/queries.js'
import { formatTokens, selectRowUsage } from '../lib/usage-accounting.js'
import { errorText, useToast } from './ui.js'

const WINDOWS = [7, 30, 90] as const
const money = (n: number | null) =>
  n === null ? 'Unknown' : n > 0 && n < 0.01 ? '<$0.01' : `$${n.toFixed(2)}`
const BASIS: Record<EfficiencyGroup['costBasis'], string> = {
  reported: 'reported',
  estimate: 'estimated',
  subscription: 'subscription share',
  mixed: 'mixed kinds',
  none: 'no cost data',
}

const full = (n: number) => n.toLocaleString('en-US')

function ExecutionSection({ runs, days }: { runs: readonly ExecutionRun[]; days: number }) {
  const projects = useMemo(
    () => executionEfficiency(runs, { now: Date.now(), windowDays: days }),
    [runs, days],
  )
  return (
    <section className="grid gap-2" aria-labelledby="prov-exec" data-execution-efficiency>
      <h3 id="prov-exec" className="m-0 text-[15px] font-semibold">
        Execution efficiency
      </h3>
      <p className="m-0 text-[14px] text-text-3">
        How runs spent tokens: by model tier, on repeated full validation and on blind exploration.
        Cost per tier is each run&apos;s cost split by its token share, not a per-tier bill.
      </p>
      {projects.length ? (
        projects.map((p) => (
          <details
            key={p.projectId}
            className="rounded-md border border-border bg-surface-1"
            data-exec-project={p.projectId}
          >
            <summary className="flex min-h-11 cursor-pointer flex-wrap items-baseline gap-x-3 px-3 py-2 text-[14px]">
              <span className="font-semibold">{p.name}</span>
              <span className="tabular-nums">{full(p.totalTokens)} tokens</span>
              <span className="tabular-nums">
                {p.findingsTotal} finding{p.findingsTotal === 1 ? '' : 's'} in {days} days
              </span>
            </summary>
            <div className="grid gap-3 px-3 pb-3">
              <table
                className="w-full text-[14px]"
                aria-label={`Tokens by model tier for ${p.name}`}
              >
                <thead>
                  <tr className="text-left text-text-3">
                    <th scope="col">Model tier</th>
                    <th scope="col">Tokens</th>
                    <th scope="col">Share</th>
                    <th scope="col">Attributed cost</th>
                  </tr>
                </thead>
                <tbody>
                  {p.tiers
                    .filter((t) => t.tokens > 0)
                    .map((t) => (
                      <tr key={t.tier} data-exec-tier={t.tier}>
                        <th scope="row" className="text-left font-normal capitalize">
                          {t.tier}
                        </th>
                        <td className="tabular-nums">{full(t.tokens)}</td>
                        <td className="tabular-nums">{(t.share * 100).toFixed(1)}%</td>
                        <td className="tabular-nums">{money(t.attributedUsd)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <dl className="stats">
                <div>
                  <dt>Repeated full validations per landed run</dt>
                  <dd data-exec-validations>
                    {p.fullValidationsPerLanded === null
                      ? 'No landed run'
                      : p.fullValidationsPerLanded.toFixed(2)}{' '}
                    ({full(p.fullValidations)} flagged, {full(p.landedRuns)} landed)
                  </dd>
                </div>
                <div>
                  <dt>Exploration share of subagent tokens</dt>
                  <dd data-exec-exploration>
                    {p.explorationShare === null
                      ? 'No subagent tokens'
                      : `${(p.explorationShare * 100).toFixed(1)}%`}{' '}
                    ({full(p.explorationTokens)} of {full(p.subagentTokens)} tokens, estimated)
                  </dd>
                </div>
                <div>
                  <dt>Findings per week (oldest first)</dt>
                  <dd data-exec-weeks>{p.weeks.map((w) => w.findings).join(', ')}</dd>
                </div>
                <div>
                  <dt>Findings by kind</dt>
                  <dd>
                    deep model on mechanical work {p.findingsById.D1}, exploration{' '}
                    {p.findingsById.D2}, repeated validation {p.findingsById.D3}, serial work{' '}
                    {p.findingsById.D4}
                  </dd>
                </div>
              </dl>
              {p.runIdsWithFindings.length ? (
                <p className="m-0 text-[14px]">
                  Runs with findings:{' '}
                  {p.runIdsWithFindings.slice(0, 12).map((id) => (
                    <Link key={id} to={`/s/${id}`} className="mr-2 text-accent underline">
                      {id}
                    </Link>
                  ))}
                </p>
              ) : null}
              <p className="m-0 text-[14px] text-text-3">
                {p.runsWithReport} of {p.runs} runs reported efficiency data.
              </p>
            </div>
          </details>
        ))
      ) : (
        <p className="m-0 text-[14px] text-text-3">Appears after runs report efficiency data.</p>
      )}
    </section>
  )
}

function GroupCard({ g, children }: { g: EfficiencyGroup; children?: React.ReactNode }) {
  return (
    <details className="rounded-md border border-border bg-surface-1" data-group={g.key}>
      <summary className="flex min-h-11 cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-[14px]">
        <span className="font-semibold">{g.label}</span>
        <span className="tabular-nums" data-metric="usd-per-outcome">
          {money(g.usdPerQualityAdjustedOutcome)} per verified outcome
        </span>
        <span className="text-text-2 tabular-nums">
          {g.outcomes} landed · {g.runs} runs · {BASIS[g.costBasis]}
          {g.missingRuns ? ` · ${g.missingRuns} without cost data` : ''}
        </span>
      </summary>
      <div className="grid gap-2 px-3 pb-3 text-[14px]">
        <dl className="stats">
          <div>
            <dt>Reported</dt>
            <dd>{money(g.reportedUsd)}</dd>
          </div>
          <div>
            <dt>Estimated</dt>
            <dd>{money(g.estimatedUsd)}</dd>
          </div>
          <div>
            <dt>Subscription share</dt>
            <dd>{money(g.subscriptionUsd)}</dd>
          </div>
          <div>
            <dt>API-equivalent</dt>
            <dd>{money(g.apiEquivalentUsd)}</dd>
          </div>
          <div>
            <dt>Tokens</dt>
            <dd>{formatTokens(g.tokens)}</dd>
          </div>
          <div>
            <dt>Corrections</dt>
            <dd>{g.correctionsKnown ? g.corrections : 'Not tracked'}</dd>
          </div>
        </dl>
        {children}
        <ul className="m-0 grid list-none gap-1 p-0">
          {g.runIds.slice(0, 20).map((id) => (
            <li key={id}>
              <Link to={`/s/${id}`} className="text-accent underline">
                Open run {id}
              </Link>
            </li>
          ))}
          {g.runIds.length > 20 ? (
            <li className="text-text-3">and {g.runIds.length - 20} more runs</li>
          ) : null}
        </ul>
      </div>
    </details>
  )
}

export function ProvidersReport() {
  const b = useBackend()
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30)
  const [billing, setBilling] = useState<Record<string, Billing>>(() =>
    b.entities ? {} : loadBilling(),
  )
  // ---- g13-digests: prices live in an encrypted, synced setting entity when available ----
  const entities = b.entities
  const toast = useToast()
  useEffect(() => {
    if (!entities) return
    void loadSyncedBilling(entities)
      .then(setBilling)
      .catch((e) => toast(errorText(e), 'error'))
  }, [entities, toast])
  const persist = (id: string, next: Billing | undefined) => {
    if (!entities) return setBilling(saveBilling(id, next))
    void saveSyncedBilling(entities, id, next)
      .then(setBilling)
      .catch((e) => toast(errorText(e), 'error'))
  }
  // ---- end g13-digests ----
  const runs = useRuns()
  const machines = useMachines()
  const projects = useProjects()
  const metrics = useMetrics(days)
  const tasks = useQuery({
    queryKey: ['provider-efficiency', 'tasks', (projects.data ?? []).map((p) => p.id)],
    enabled: !!projects.data,
    retry: false,
    queryFn: async (): Promise<EfficiencyTask[]> =>
      (await Promise.all((projects.data ?? []).map((p) => b.api.tasks({ projectId: p.id }))))
        .flat()
        .map((t) => ({
          id: t.id,
          title: t.title,
          labels: t.labels,
          status: t.status,
          landedAt: t.landedAt,
          runId: t.runId,
        })),
  })

  const accountLabels = useMemo(() => {
    const m = new Map<string, string>()
    for (const mc of machines.data ?? [])
      for (const a of mc.providerAccounts ?? []) m.set(a.id, a.label)
    return m
  }, [machines.data])

  const effRuns = useMemo(
    (): EfficiencyRun[] =>
      (runs.data ?? []).flatMap((r) => {
        if (r.agent !== 'claude' && r.agent !== 'codex') return []
        const model = r.model ?? r.usageReport?.models?.[0]
        return [
          {
            runId: r.runId,
            agent: r.agent,
            accountId: r.accountId ?? r.agent,
            ...(model ? { model } : {}),
            ...(r.taskId ? { taskId: r.taskId } : {}),
            ...(r.title ? { title: r.title } : {}),
            updatedAt: r.updatedAt ?? r.startedAt ?? 0,
            usage: selectRowUsage(r),
            ...(r.landing?.state === 'landed' ? { landed: true } : {}),
          },
        ]
      }),
    [runs.data],
  )

  const report = useMemo(
    () =>
      analyzeProviderEfficiency({
        now: Date.now(),
        windowDays: days,
        runs: effRuns,
        tasks: tasks.data ?? [],
        billing,
        ...(metrics.data?.subscription ? { utilization: metrics.data.subscription } : {}),
      }),
    [effRuns, tasks.data, billing, metrics.data, days],
  )
  const execRuns = useMemo(
    (): ExecutionRun[] =>
      (runs.data ?? []).flatMap((r) => {
        if (!r.projectId) return []
        const c = selectRowUsage(r).charge
        return [
          {
            runId: r.runId,
            projectId: r.projectId,
            ...(r.projectName ? { projectName: r.projectName } : {}),
            updatedAt: r.updatedAt ?? r.startedAt ?? 0,
            ...(c.kind === 'reported' || c.kind === 'estimate' ? { usd: c.usd } : {}),
            ...(r.landing?.state === 'landed' ? { landed: true } : {}),
            ...(r.usageReport?.children ? { subagentTokens: r.usageReport.children.tokens } : {}),
            ...(r.usageReport?.efficiency ? { efficiency: r.usageReport.efficiency } : {}),
          },
        ]
      }),
    [runs.data],
  )
  const accounts = [
    ...new Set([...effRuns.map((r) => r.accountId), ...Object.keys(billing)]),
  ].sort()

  const exportJson = () => {
    const doc = exportRoutingRecommendations(report, Date.now())
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' }),
    )
    const a = document.createElement('a')
    a.href = url
    a.download = 'viko-routing-recommendations.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (runs.isSuccess && !effRuns.length)
    return (
      <p className="m-0 px-4 text-[14px] text-text-3">
        Provider comparisons appear after runs report their usage.
      </p>
    )

  return (
    <div className="grid gap-4 px-4" id="providers" data-providers-report>
      <p className="m-0 text-[14px] text-text-2">
        Cost per verified outcome: spend on every attempt divided by tasks that landed. Reported is
        what the provider CLI stated; estimated is computed from tokens with API list prices dated{' '}
        {report.priceTableAsOf}; runs with neither are counted as missing, never as free.
      </p>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Report window">
        {WINDOWS.map((w) => (
          <Button key={w} variant={w === days ? 'primary' : 'ghost'} onClick={() => setDays(w)}>
            {w} days
          </Button>
        ))}
      </div>

      <section className="grid gap-2" aria-labelledby="prov-billing">
        <h3 id="prov-billing" className="m-0 text-[15px] font-semibold">
          Account billing
        </h3>
        <p className="m-0 text-[14px] text-text-3">
          Leave blank if unknown. The price stays on this device.
        </p>
        {accounts.map((id) => {
          const cur = billing[id]
          return (
            <div key={id} className="flex flex-wrap items-center gap-2" data-billing-account={id}>
              <span className="min-w-[8rem] text-[14px]">{accountLabels.get(id) ?? id}</span>
              <select
                className="input"
                aria-label={`Billing for ${id}`}
                value={cur?.kind ?? 'unknown'}
                onChange={(e) => {
                  const k = e.target.value
                  persist(
                    id,
                    k === 'api'
                      ? { kind: 'api' }
                      : k === 'subscription'
                        ? { kind: 'subscription', monthlyUsd: 20 }
                        : undefined,
                  )
                }}
              >
                <option value="unknown">Unknown</option>
                <option value="subscription">Subscription</option>
                <option value="api">Billed per token (API)</option>
              </select>
              {cur?.kind === 'subscription' ? (
                <label className="flex items-center gap-1 text-[14px]">
                  <span>USD per month</span>
                  <input
                    className="input w-28"
                    type="number"
                    min="1"
                    step="1"
                    defaultValue={cur.monthlyUsd}
                    aria-label={`Monthly price for ${id}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value)
                      if (v > 0) persist(id, { kind: 'subscription', monthlyUsd: v })
                    }}
                  />
                </label>
              ) : null}
            </div>
          )
        })}
        {report.billingUnknown.length ? (
          <p className="m-0 text-[14px] text-status-wait">
            Billing unknown for {report.billingUnknown.join(', ')}: their costs are API-equivalent
            figures, not what you were charged.
          </p>
        ) : null}
      </section>

      <section className="grid gap-2" aria-labelledby="prov-subs">
        <h3 id="prov-subs" className="m-0 text-[15px] font-semibold">
          Subscription utilization
        </h3>
        {report.subscriptions.length ? (
          report.subscriptions.map((s) => (
            <details
              key={s.accountId}
              className="rounded-md border border-border bg-surface-1"
              data-subscription={s.accountId}
            >
              <summary className="flex min-h-11 cursor-pointer flex-wrap items-baseline gap-x-3 px-3 py-2 text-[14px]">
                <span className="font-semibold">
                  {accountLabels.get(s.accountId) ?? s.accountId}
                </span>
                <span className="tabular-nums">
                  {s.utilizationPct === null
                    ? 'Utilization unknown'
                    : `${Math.round(s.utilizationPct)}% of quota used`}
                </span>
                <span className="text-text-2">
                  {s.verdict === 'unknown'
                    ? 'API comparison unavailable'
                    : s.verdict === 'cheaper_than_api'
                      ? 'Cheaper than API pricing'
                      : 'Costlier than API pricing'}
                </span>
              </summary>
              <dl className="stats px-3 pb-3">
                <div>
                  <dt>Cost for window</dt>
                  <dd>{money(s.windowCostUsd)}</dd>
                </div>
                <div>
                  <dt>API-equivalent of usage</dt>
                  <dd>{money(s.apiEquivalentUsd)}</dd>
                </div>
                <div>
                  <dt>Value vs API</dt>
                  <dd>{s.valueRatio === null ? 'Unknown' : `${s.valueRatio.toFixed(2)}x`}</dd>
                </div>
                <div>
                  <dt>Idle capacity</dt>
                  <dd>{money(s.idleUsd)}</dd>
                </div>
                <div>
                  <dt>Per outcome</dt>
                  <dd>{money(s.usdPerOutcome)}</dd>
                </div>
                <div>
                  <dt>Per outcome at full use</dt>
                  <dd>{money(s.usdPerOutcomeAtFullUse)}</dd>
                </div>
              </dl>
            </details>
          ))
        ) : (
          <p className="m-0 text-[14px] text-text-3">
            Set a subscription price above to see utilization and the subscription-versus-API
            comparison.
          </p>
        )}
      </section>

      {(
        [
          ['Provider', report.byProvider],
          ['Model', report.byModel],
          ['Account', report.byAccount],
        ] as const
      ).map(([title, groups]) => (
        <section key={title} className="grid gap-2" aria-label={`By ${title.toLowerCase()}`}>
          <h3 className="m-0 text-[15px] font-semibold">By {title.toLowerCase()}</h3>
          {groups.map((g) => (
            <GroupCard
              key={g.key}
              g={title === 'Account' ? { ...g, label: accountLabels.get(g.key) ?? g.label } : g}
            />
          ))}
        </section>
      ))}

      <section className="grid gap-2" aria-labelledby="prov-types">
        <h3 id="prov-types" className="m-0 text-[15px] font-semibold">
          By task type
        </h3>
        {report.byTaskType.map((t) => (
          <GroupCard key={t.type} g={t.total}>
            <div className="grid gap-2">
              {t.candidates.map((c) => (
                <GroupCard key={c.key} g={c} />
              ))}
            </div>
          </GroupCard>
        ))}
      </section>

      <ExecutionSection runs={execRuns} days={days} />

      <section className="grid gap-2" aria-labelledby="prov-recs">
        <h3 id="prov-recs" className="m-0 text-[15px] font-semibold">
          Routing recommendations
        </h3>
        {report.recommendations.length ? (
          <ul className="m-0 grid list-none gap-2 p-0">
            {report.recommendations.map((r) => (
              <li key={r.taskType} className="text-[14px]" data-recommendation={r.taskType}>
                <strong>{r.taskType}</strong>: {r.preferred.provider}
                {r.preferred.model ? ` (${r.preferred.model})` : ''}
                {r.accountId ? ` on ${accountLabels.get(r.accountId) ?? r.accountId}` : ''} ·{' '}
                {r.confidence} confidence. {r.reason}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-[14px] text-text-3">
            Not enough landed work on two or more providers per task type yet.
          </p>
        )}
        <div>
          <Button disabled={!report.recommendations.length} onClick={exportJson}>
            Export recommendations
          </Button>
        </div>
      </section>
    </div>
  )
}
